/**
 * Admin Dashboard
 * Manage reorder queue, view pending requests, and access supplier cart links
 */
import React, { useState, useEffect, useCallback } from 'react';
import { assetsAPI, reorderAPI } from '../services/api';
import { Asset, ReorderRequest } from '../types';
import '../styles/AdminDashboard.css';

// Modal Types
interface MarkOrderedModalData {
  orderNumber: string;
  estimatedDelivery: string;
  actualCost: string;
}

interface MarkReceivedModalData {
  actualDelivery: string;
}

interface UpdateTrackingModalData {
  trackingNumber: string;
  carrier: string;
  expectedDeliveryDate: string;
  trackingUrl: string;
}

interface CancelModalData {
  notes: string;
}

// Modal Component for Mark Ordered
interface MarkOrderedModalProps {
  isOpen: boolean;
  itemName: string;
  onClose: () => void;
  onSubmit: (data: MarkOrderedModalData) => void;
  isSubmitting: boolean;
}

const MarkOrderedModal: React.FC<MarkOrderedModalProps> = ({
  isOpen,
  itemName,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState<MarkOrderedModalData>({
    orderNumber: '',
    estimatedDelivery: '',
    actualCost: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ orderNumber: '', estimatedDelivery: '', actualCost: '' });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Mark as Ordered</h2>
          <button onClick={onClose} className="close-btn" disabled={isSubmitting}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="modal-item-name">Item: <strong>{itemName}</strong></p>
            
            <div className="form-group">
              <label htmlFor="orderNumber">Order Number (optional)</label>
              <input
                type="text"
                id="orderNumber"
                name="orderNumber"
                value={formData.orderNumber}
                onChange={handleChange}
                placeholder="e.g., PO-2025-0001"
                disabled={isSubmitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="estimatedDelivery">Estimated Delivery Date (optional)</label>
              <input
                type="date"
                id="estimatedDelivery"
                name="estimatedDelivery"
                value={formData.estimatedDelivery}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="actualCost">Actual Cost (optional)</label>
              <input
                type="number"
                id="actualCost"
                name="actualCost"
                value={formData.actualCost}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                disabled={isSubmitting}
              />
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              onClick={onClose} 
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Mark as Ordered'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal Component for Mark Received
interface MarkReceivedModalProps {
  isOpen: boolean;
  itemName: string;
  onClose: () => void;
  onSubmit: (data: MarkReceivedModalData) => void;
  isSubmitting: boolean;
}

const MarkReceivedModal: React.FC<MarkReceivedModalProps> = ({
  isOpen,
  itemName,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState<MarkReceivedModalData>({
    actualDelivery: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      // Default to today's date
      setFormData({ actualDelivery: new Date().toISOString().split('T')[0] });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Mark as Received</h2>
          <button onClick={onClose} className="close-btn" disabled={isSubmitting}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="modal-item-name">Item: <strong>{itemName}</strong></p>
            
            <div className="form-group">
              <label htmlFor="actualDelivery">Actual Delivery Date</label>
              <input
                type="date"
                id="actualDelivery"
                name="actualDelivery"
                value={formData.actualDelivery}
                onChange={e => setFormData({ actualDelivery: e.target.value })}
                disabled={isSubmitting}
              />
              <small>Defaults to today if left empty</small>
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              onClick={onClose} 
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Mark as Received'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal Component for Update Tracking
interface UpdateTrackingModalProps {
  isOpen: boolean;
  itemName: string;
  onClose: () => void;
  onSubmit: (data: UpdateTrackingModalData) => void;
  isSubmitting: boolean;
}

const UpdateTrackingModal: React.FC<UpdateTrackingModalProps> = ({
  isOpen,
  itemName,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState<UpdateTrackingModalData>({
    trackingNumber: '',
    carrier: '',
    expectedDeliveryDate: '',
    trackingUrl: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        trackingNumber: '',
        carrier: '',
        expectedDeliveryDate: '',
        trackingUrl: '',
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Update Tracking Information</h2>
          <button onClick={onClose} className="close-btn" disabled={isSubmitting}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="modal-item-name">Item: <strong>{itemName}</strong></p>
            
            <div className="form-group">
              <label htmlFor="trackingNumber">Tracking Number (optional)</label>
              <input
                type="text"
                id="trackingNumber"
                name="trackingNumber"
                value={formData.trackingNumber}
                onChange={handleChange}
                placeholder="e.g., 1Z999AA10123456784"
                disabled={isSubmitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="carrier">Carrier/Shipper (optional)</label>
              <input
                type="text"
                id="carrier"
                name="carrier"
                value={formData.carrier}
                onChange={handleChange}
                placeholder="e.g., UPS, FedEx, USPS"
                disabled={isSubmitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="expectedDeliveryDate">Expected Delivery Date (optional)</label>
              <input
                type="date"
                id="expectedDeliveryDate"
                name="expectedDeliveryDate"
                value={formData.expectedDeliveryDate}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="trackingUrl">Tracking URL (optional)</label>
              <input
                type="url"
                id="trackingUrl"
                name="trackingUrl"
                value={formData.trackingUrl}
                onChange={handleChange}
                placeholder="https://..."
                disabled={isSubmitting}
              />
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              onClick={onClose} 
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Update Tracking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal Component for Cancel Request
interface CancelModalProps {
  isOpen: boolean;
  itemName: string;
  onClose: () => void;
  onSubmit: (data: CancelModalData) => void;
  isSubmitting: boolean;
}

const CancelModal: React.FC<CancelModalProps> = ({
  isOpen,
  itemName,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState<CancelModalData>({
    notes: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ notes: '' });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Cancel Request</h2>
          <button onClick={onClose} className="close-btn" disabled={isSubmitting}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="modal-item-name">Item: <strong>{itemName}</strong></p>
            
            <div className="form-group">
              <label htmlFor="notes">Reason for Cancellation</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={e => setFormData({ notes: e.target.value })}
                placeholder="Please provide a reason for cancellation..."
                rows={3}
                disabled={isSubmitting}
              />
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              onClick={onClose} 
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Keep Request
            </button>
            <button 
              type="submit" 
              className="btn-danger"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Cancelling...' : 'Cancel Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const [requests, setRequests] = useState<ReorderRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const [supplierGroups, setSupplierGroups] = useState<any>(null);
  const [notCheckedInAssets, setNotCheckedInAssets] = useState<Asset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  
  // Modal state
  const [selectedRequest, setSelectedRequest] = useState<ReorderRequest | null>(null);
  const [showMarkOrderedModal, setShowMarkOrderedModal] = useState(false);
  const [showMarkReceivedModal, setShowMarkReceivedModal] = useState(false);
  const [showUpdateTrackingModal, setShowUpdateTrackingModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      if (filter === 'pending') {
        const response = await reorderAPI.getPendingRequests();
        setRequests(response.data);
      } else {
        const response = await reorderAPI.listRequests();
        setRequests(response.data.results);
      }
    } catch (err) {
      console.error('Error loading requests:', err);
      alert('Failed to load requests. Please log in.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadNotCheckedInAssets = useCallback(async () => {
    try {
      setLoadingAssets(true);
      const response = await assetsAPI.getNotCheckedIn();
      setNotCheckedInAssets(response.data);
    } catch (err) {
      console.error('Error loading assets not checked in:', err);
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
    loadNotCheckedInAssets();
  }, [loadRequests, loadNotCheckedInAssets]);

  const loadSupplierGroups = async () => {
    try {
      const response = await reorderAPI.getBySupplier();
      setSupplierGroups(response.data);
    } catch (err) {
      console.error('Error loading supplier groups:', err);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await reorderAPI.approveRequest(id);
      loadRequests();
      alert('Request approved');
    } catch (err) {
      console.error('Error approving request:', err);
      alert('Failed to approve request');
    }
  };

  // Open modals
  const openMarkOrderedModal = (request: ReorderRequest) => {
    setSelectedRequest(request);
    setShowMarkOrderedModal(true);
  };

  const openMarkReceivedModal = (request: ReorderRequest) => {
    setSelectedRequest(request);
    setShowMarkReceivedModal(true);
  };

  const openUpdateTrackingModal = (request: ReorderRequest) => {
    setSelectedRequest(request);
    setShowUpdateTrackingModal(true);
  };

  const openCancelModal = (request: ReorderRequest) => {
    setSelectedRequest(request);
    setShowCancelModal(true);
  };

  // Close modals
  const closeAllModals = () => {
    setShowMarkOrderedModal(false);
    setShowMarkReceivedModal(false);
    setShowUpdateTrackingModal(false);
    setShowCancelModal(false);
    setSelectedRequest(null);
  };

  // Submit handlers
  const handleMarkOrderedSubmit = async (data: MarkOrderedModalData) => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      const apiData: any = {};
      if (data.orderNumber) apiData.order_number = data.orderNumber;
      if (data.estimatedDelivery) apiData.estimated_delivery = data.estimatedDelivery;
      if (data.actualCost) apiData.actual_cost = parseFloat(data.actualCost);

      await reorderAPI.markOrdered(selectedRequest.id, apiData);
      loadRequests();
      closeAllModals();
      alert('Marked as ordered successfully');
    } catch (err) {
      console.error('Error marking as ordered:', err);
      alert('Failed to mark as ordered');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkReceivedSubmit = async (data: MarkReceivedModalData) => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      await reorderAPI.markReceived(selectedRequest.id, data.actualDelivery || undefined);
      loadRequests();
      closeAllModals();
      alert('Marked as received and inventory updated');
    } catch (err) {
      console.error('Error marking as received:', err);
      alert('Failed to mark as received');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTrackingSubmit = async (data: UpdateTrackingModalData) => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      const apiData: any = {};
      if (data.trackingNumber) apiData.tracking_number = data.trackingNumber;
      if (data.carrier) apiData.carrier = data.carrier;
      if (data.expectedDeliveryDate) apiData.expected_delivery_date = data.expectedDeliveryDate;
      if (data.trackingUrl) apiData.delivery_tracking_url = data.trackingUrl;

      await reorderAPI.updateTracking(selectedRequest.id, apiData);
      loadRequests();
      closeAllModals();
      alert('Tracking information updated');
    } catch (err) {
      console.error('Error updating tracking:', err);
      alert('Failed to update tracking information');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubmit = async (data: CancelModalData) => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      await reorderAPI.cancelRequest(selectedRequest.id, data.notes);
      loadRequests();
      closeAllModals();
      alert('Request cancelled');
    } catch (err) {
      console.error('Error cancelling request:', err);
      alert('Failed to cancel request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPriorityClass = (priority: string) => {
    const map: Record<string, string> = {
      urgent: 'priority-urgent',
      high: 'priority-high',
      normal: 'priority-normal',
      low: 'priority-low',
    };
    return map[priority] || '';
  };

  const getStatusClass = (status: string) => {
    const map: Record<string, string> = {
      pending: 'status-pending',
      approved: 'status-approved',
      ordered: 'status-ordered',
      received: 'status-received',
      cancelled: 'status-cancelled',
    };
    return map[status] || '';
  };

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <div className="header-actions">
          <button onClick={loadSupplierGroups} className="btn-secondary">
            View by Supplier
          </button>
        </div>
      </header>

      <div className="filter-bar">
        <button
          className={filter === 'pending' ? 'active' : ''}
          onClick={() => setFilter('pending')}
        >
          Pending ({requests.filter(r => r.status === 'pending').length})
        </button>
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All Requests
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading requests...</div>
      ) : (
        <div className="requests-table">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Requested By</th>
                <th>Requested</th>
                <th>Est. Cost</th>
                <th>Lead Time</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr>
                  <td colSpan={9} className="no-data">
                    No requests found
                  </td>
                </tr>
              ) : (
                requests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <div className="item-cell">
                        {request.item_details.thumbnail && (
                          <img
                            src={request.item_details.thumbnail}
                            alt={request.item_details.name}
                            className="item-thumb"
                          />
                        )}
                        <div>
                          <div className="item-name">{request.item_details.name}</div>
                          {request.item_details.supplier_name && (
                            <div className="item-supplier">
                              {request.item_details.supplier_name}
                            </div>
                          )}

                          {/* Order tracking information */}
                          {request.order_number && (
                            <div className="tracking-info">
                              <small>📋 Order: {request.order_number}</small>
                            </div>
                          )}

                          {request.estimated_delivery && (
                            <div className="delivery-info">
                              <small>
                                📅 Expected: {new Date(request.estimated_delivery).toLocaleDateString()}
                              </small>
                            </div>
                          )}

                          {request.actual_delivery && (
                            <div className="delivery-info">
                              <small>
                                ✅ Delivered: {new Date(request.actual_delivery).toLocaleDateString()}
                              </small>
                            </div>
                          )}

                          {request.status === 'ordered' && request.estimated_delivery && (
                            <div className="delivery-status">
                              <small>
                                {(() => {
                                  const estimatedDate = new Date(request.estimated_delivery);
                                  const today = new Date();
                                  const diffTime = estimatedDate.getTime() - today.getTime();
                                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                                  if (diffDays < 0) {
                                    return <span className="overdue">⚠️ {Math.abs(diffDays)} days overdue</span>;
                                  } else if (diffDays === 0) {
                                    return <span className="due-today">🚚 Expected today</span>;
                                  } else if (diffDays === 1) {
                                    return <span className="due-soon">📦 Expected tomorrow</span>;
                                  } else {
                                    return <span className="due-later">📅 {diffDays} days to go</span>;
                                  }
                                })()}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>{request.quantity}</td>
                    <td>
                      <span className={`priority-badge ${getPriorityClass(request.priority)}`}>
                        {request.priority}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(request.status)}`}>
                        {request.status}
                      </span>
                    </td>
                    <td>{request.requested_by || 'Anonymous'}</td>
                    <td>{new Date(request.requested_at).toLocaleDateString()}</td>
                    <td>
                      {request.estimated_cost
                        ? `$${parseFloat(request.estimated_cost).toFixed(2)}`
                        : '-'}
                    </td>
                    <td>{request.item_details.average_lead_time} days</td>
                    <td>
                      <div className="action-buttons">
                        {request.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(request.id)}
                              className="btn-approve"
                              title="Approve"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => openCancelModal(request)}
                              className="btn-cancel"
                              title="Cancel"
                            >
                              ✗
                            </button>
                          </>
                        )}
                        {request.status === 'approved' && (
                          <button
                            onClick={() => openMarkOrderedModal(request)}
                            className="btn-order"
                          >
                            Mark Ordered
                          </button>
                        )}
                        {request.status === 'ordered' && (
                          <>
                            <button
                              onClick={() => openUpdateTrackingModal(request)}
                              className="btn-tracking"
                              title="Update Tracking"
                            >
                              📦
                            </button>
                            <button
                              onClick={() => openMarkReceivedModal(request)}
                              className="btn-receive"
                            >
                              Mark Received
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {supplierGroups && (
        <div className="supplier-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Requests by Supplier</h2>
              <button onClick={() => setSupplierGroups(null)} className="close-btn">
                ✕
              </button>
            </div>
            <div className="modal-body">
              {supplierGroups.map((group: any) => (
                <div key={group.supplier} className="supplier-group">
                  <h3>{group.supplier}</h3>
                  <p>
                    {group.item_count} items - Est. Total: $
                    {group.total_estimated_cost.toFixed(2)}
                  </p>
                  <ul>
                    {group.requests.map((req: any) => (
                      <li key={req.id}>
                        {req.item_details.name} - Qty: {req.quantity}
                        {req.item_details.supplier_url && (
                          <a
                            href={req.item_details.supplier_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="supplier-link"
                          >
                            View on {group.supplier}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Assets Not Checked In Section */}
      <div className="assets-section">
        <h2>Assets Not Checked In (3+ Months)</h2>
        {loadingAssets ? (
          <div className="loading">Loading assets...</div>
        ) : (
          <>
            {notCheckedInAssets.length === 0 ? (
              <p className="no-data">All assets have been checked in recently.</p>
            ) : (
              <div className="assets-table">
                <table>
                  <thead>
                    <tr>
                      <th>Asset Name</th>
                      <th>Asset Tag</th>
                      <th>Location</th>
                      <th>Last Scanned</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notCheckedInAssets.map((asset) => (
                      <tr key={asset.id}>
                        <td>{asset.name}</td>
                        <td>{asset.asset_tag || '—'}</td>
                        <td>{asset.location_name || '—'}</td>
                        <td>
                          {asset.last_scanned_at
                            ? new Date(asset.last_scanned_at).toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td>
                          <span className={`status-badge status-${asset.status}`}>
                            {asset.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <MarkOrderedModal
        isOpen={showMarkOrderedModal}
        itemName={selectedRequest?.item_details?.name || ''}
        onClose={closeAllModals}
        onSubmit={handleMarkOrderedSubmit}
        isSubmitting={isSubmitting}
      />
      
      <MarkReceivedModal
        isOpen={showMarkReceivedModal}
        itemName={selectedRequest?.item_details?.name || ''}
        onClose={closeAllModals}
        onSubmit={handleMarkReceivedSubmit}
        isSubmitting={isSubmitting}
      />
      
      <UpdateTrackingModal
        isOpen={showUpdateTrackingModal}
        itemName={selectedRequest?.item_details?.name || ''}
        onClose={closeAllModals}
        onSubmit={handleUpdateTrackingSubmit}
        isSubmitting={isSubmitting}
      />
      
      <CancelModal
        isOpen={showCancelModal}
        itemName={selectedRequest?.item_details?.name || ''}
        onClose={closeAllModals}
        onSubmit={handleCancelSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default AdminDashboard;
